import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { PromangRichTextEditor } from "../../../components/utils/PromangRichTextEditor";
import "../../../components/utils/PromangRichTextEditor.css";

export function emptyGroup(order = 0) {
  return {
    key: `g-${Date.now()}-${order}`,
    title: "",
    description: "",
    order,
    entries: [emptyEntry(0)],
  };
}

export function emptyEntry(order = 0) {
  return {
    key: `e-${Date.now()}-${order}`,
    description: "",
    deadline: "",
    order,
  };
}

export function MeetingMinutesAgendaEditor({ groups, onChange }) {
  const { t } = useTranslation();

  const updateGroups = (next) => onChange(next);

  const addGroup = () => {
    updateGroups([...(groups || []), emptyGroup(groups?.length || 0)]);
  };

  const updateGroup = (index, patch) => {
    updateGroups((groups || []).map((group, i) => (i === index ? { ...group, ...patch } : group)));
  };

  const removeGroup = (index) => {
    updateGroups((groups || []).filter((_, i) => i !== index));
  };

  const addEntry = (groupIndex) => {
    const group = groups[groupIndex];
    updateGroup(groupIndex, {
      entries: [...(group.entries || []), emptyEntry(group.entries?.length || 0)],
    });
  };

  const updateEntry = (groupIndex, entryIndex, patch) => {
    const group = groups[groupIndex];
    updateGroup(groupIndex, {
      entries: (group.entries || []).map((entry, i) =>
        i === entryIndex ? { ...entry, ...patch } : entry
      ),
    });
  };

  const removeEntry = (groupIndex, entryIndex) => {
    const group = groups[groupIndex];
    updateGroup(groupIndex, {
      entries: (group.entries || []).filter((_, i) => i !== entryIndex),
    });
  };

  return (
    <div className="space-y-4">
      {(groups || []).map((group, groupIndex) => (
        <div key={group.id || group.key || groupIndex} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex-1 space-y-3">
              <input
                type="text"
                value={group.title || ""}
                onChange={(e) => updateGroup(groupIndex, { title: e.target.value })}
                placeholder={t("meeting_minutes.point.title")}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
              />
              <textarea
                value={group.description || ""}
                onChange={(e) => updateGroup(groupIndex, { description: e.target.value })}
                placeholder={t("meeting_minutes.point.description")}
                rows={2}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
              />
            </div>
            <button
              type="button"
              onClick={() => removeGroup(groupIndex)}
              className="rounded-full p-2 text-red-600 hover:bg-red-50"
              aria-label={t("meeting_minutes.group.delete")}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-3">
            {(group.entries || []).map((entry, entryIndex) => (
              <div key={entry.id || entry.key || entryIndex} className="rounded-xl border border-slate-200 bg-white p-3">
                <PromangRichTextEditor
                  value={entry.description || ""}
                  onChange={(html) => updateEntry(groupIndex, entryIndex, { description: html })}
                  placeholder={t("meeting_minutes.entry.description")}
                  size="sm"
                  framed
                  templatesEnabled={false}
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeEntry(groupIndex, entryIndex)}
                    className="text-xs font-semibold text-red-600 hover:underline"
                  >
                    {t("meeting_minutes.entry.remove")}
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addEntry(groupIndex)}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#1463C7]"
            >
              <PlusIcon className="h-4 w-4" />
              {t("meeting_minutes.entry.add")}
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addGroup}
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        <PlusIcon className="h-4 w-4" />
        {t("meeting_minutes.add_point")}
      </button>
    </div>
  );
}
